import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Inbox, MessageSquareQuote, MapPin, Settings as SettingsIcon, Loader2 } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";

const inputCls = "focus-ring w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-teal-500";

const TABS = [
  { id: "enquiries", label: "Enquiries", icon: Inbox },
  { id: "testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { id: "areas", label: "Service Areas", icon: MapPin },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

const statusStyles = {
  new: "bg-teal-100 text-teal-800",
  contacted: "bg-amber-100 text-amber-800",
  closed: "bg-slate-200 text-slate-600",
};

function EnquiriesTab() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/admin/enquiries").then((r) => setItems(r.data)).catch(() => toast.error("Could not load enquiries")), []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/enquiries/${id}`, { status });
    setItems((arr) => arr.map((e) => (e._id === id ? { ...e, status } : e)));
    toast.success("Status updated");
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await api.delete(`/admin/enquiries/${id}`);
    setItems((arr) => arr.filter((e) => e._id !== id));
    toast.success("Enquiry deleted");
  };

  if (!items) return <Loader2 className="h-6 w-6 animate-spin text-teal-600" />;
  if (!items.length) return <p className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500" data-testid="enquiries-empty">No enquiries yet. New quote requests will appear here.</p>;

  return (
    <div className="space-y-4" data-testid="enquiries-list">
      {items.map((e, i) => (
        <article key={e._id} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`enquiry-card-${i}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="font-display text-base font-bold text-navy">{e.full_name} <span className="ml-2 text-sm font-medium text-slate-500">{e.service}</span></h3>
              <p className="mt-1 text-xs text-slate-500">{new Date(e.created_at).toLocaleString("en-AU")}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  localStorage.setItem("rd_doc_prefill", JSON.stringify({
                    custName: e.full_name,
                    custSuburb: e.suburb,
                    custPhone: e.phone,
                    custEmail: e.email,
                    items: [{ desc: `${e.service}${e.property_size ? ` — ${e.property_size}` : ""}${e.frequency ? ` (${e.frequency})` : ""}`, qty: 1, rate: "" }],
                    notes: e.message ? `Customer message: ${e.message}` : "Price includes all labour, equipment and standard cleaning products.",
                  }));
                  window.location.href = "/admin/documents";
                }}
                className="focus-ring inline-flex items-center gap-1.5 rounded-full bg-teal-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-teal-700"
                data-testid={`enquiry-quote-${i}`}
              >
                + Create Quote
              </button>
              <span className={`rounded-full px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest ${statusStyles[e.status] || statusStyles.new}`}>{e.status}</span>
              <select aria-label="Enquiry status" className={inputCls + " !w-auto !py-1.5 text-xs"} value={e.status} onChange={(ev) => setStatus(e._id, ev.target.value)} data-testid={`enquiry-status-${i}`}>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="closed">Closed</option>
              </select>
              <button type="button" onClick={() => remove(e._id)} className="focus-ring rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600" aria-label="Delete enquiry" data-testid={`enquiry-delete-${i}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          <dl className="mt-4 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <div><dt className="text-xs text-slate-500">Phone</dt><dd className="font-medium text-slate-800"><a className="hover:text-teal-700" href={`tel:${e.phone}`}>{e.phone}</a></dd></div>
            <div><dt className="text-xs text-slate-500">Email</dt><dd className="font-medium text-slate-800"><a className="hover:text-teal-700" href={`mailto:${e.email}`}>{e.email}</a></dd></div>
            <div><dt className="text-xs text-slate-500">Suburb</dt><dd className="font-medium text-slate-800">{e.suburb || "—"}</dd></div>
            <div><dt className="text-xs text-slate-500">Property Type</dt><dd className="font-medium text-slate-800">{e.property_type || "—"}</dd></div>
            <div><dt className="text-xs text-slate-500">Preferred</dt><dd className="font-medium text-slate-800">{[e.preferred_date, e.preferred_time].filter(Boolean).join(" · ") || "—"}</dd></div>
            <div><dt className="text-xs text-slate-500">Frequency / Size</dt><dd className="font-medium text-slate-800">{[e.frequency, e.property_size].filter(Boolean).join(" · ") || "—"}</dd></div>
          </dl>
          <p className="mt-4 rounded-xl bg-slate-50 p-4 text-sm leading-relaxed text-slate-700">{e.message}</p>
        </article>
      ))}
    </div>
  );
}

function TestimonialsTab() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ quote: "", author: "", detail: "", published: true });
  const load = useCallback(() => api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => toast.error("Could not load testimonials")), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    const { data } = await api.post("/admin/testimonials", form);
    setItems((arr) => [...arr, data]);
    setForm({ quote: "", author: "", detail: "", published: true });
    toast.success("Testimonial added");
  };
  const toggle = async (t) => {
    await api.patch(`/admin/testimonials/${t._id}`, { quote: t.quote, author: t.author, detail: t.detail, published: !t.published });
    setItems((arr) => arr.map((x) => (x._id === t._id ? { ...x, published: !x.published } : x)));
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    setItems((arr) => arr.filter((x) => x._id !== id));
    toast.success("Testimonial deleted");
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <form onSubmit={add} className="h-fit rounded-2xl border border-slate-200 bg-white p-6 lg:sticky lg:top-24" data-testid="testimonial-add-form">
        <h3 className="font-display text-base font-bold text-navy">Add a Real Testimonial</h3>
        <p className="mt-1 text-xs text-slate-500">Replace the placeholders with genuine customer reviews.</p>
        <div className="mt-5 space-y-4">
          <textarea required rows={3} className={inputCls} placeholder="Customer review text…" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} data-testid="testimonial-quote-input" />
          <input required className={inputCls} placeholder="Customer name, e.g. Sarah M." value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="testimonial-author-input" />
          <input className={inputCls} placeholder="Detail, e.g. Scarborough — Home Cleaning" value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} data-testid="testimonial-detail-input" />
          <button type="submit" className="focus-ring w-full rounded-full bg-teal-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-teal-700" data-testid="testimonial-add-button">Add Testimonial</button>
        </div>
      </form>
      <div className="space-y-4 lg:col-span-2" data-testid="testimonials-admin-list">
        {!items ? <Loader2 className="h-6 w-6 animate-spin text-teal-600" /> : items.map((t, i) => (
          <article key={t._id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`testimonial-admin-card-${i}`}>
            <p className="text-sm text-slate-700">"{t.quote}"</p>
            <p className="mt-2 text-xs font-semibold text-navy">{t.author} {t.detail && <span className="font-normal text-slate-500">· {t.detail}</span>}</p>
            <div className="mt-3 flex items-center gap-2">
              {t.is_placeholder && <span className="rounded-full bg-amber-50 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-amber-600">Unverified</span>}
              <button type="button" onClick={() => toggle(t)} className="focus-ring rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-teal-500 hover:text-teal-700" data-testid={`testimonial-toggle-${i}`}>
                {t.published ? "Unpublish" : "Publish"}
              </button>
              <button type="button" onClick={() => remove(t._id)} className="focus-ring rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" aria-label="Delete testimonial" data-testid={`testimonial-delete-${i}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function AreasTab() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ name: "", suburbs: "" });
  const load = useCallback(() => api.get("/content/service-areas").then((r) => setItems(r.data)).catch(() => toast.error("Could not load areas")), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    const { data } = await api.post("/admin/service-areas", form);
    setItems((arr) => [...arr, data]);
    setForm({ name: "", suburbs: "" });
    toast.success("Service area added");
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this service area?")) return;
    await api.delete(`/admin/service-areas/${id}`);
    setItems((arr) => arr.filter((x) => x._id !== id));
    toast.success("Service area deleted");
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <form onSubmit={add} className="h-fit rounded-2xl border border-slate-200 bg-white p-6 lg:sticky lg:top-24" data-testid="area-add-form">
        <h3 className="font-display text-base font-bold text-navy">Add a Service Area</h3>
        <div className="mt-5 space-y-4">
          <input required className={inputCls} placeholder="e.g. Northern Suburbs" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="area-name-input" />
          <input className={inputCls} placeholder="Suburbs, e.g. Joondalup, Scarborough" value={form.suburbs} onChange={(e) => setForm({ ...form, suburbs: e.target.value })} data-testid="area-suburbs-input" />
          <button type="submit" className="focus-ring w-full rounded-full bg-teal-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-teal-700" data-testid="area-add-button">Add Area</button>
        </div>
      </form>
      <div className="space-y-4 lg:col-span-2" data-testid="areas-admin-list">
        {!items ? <Loader2 className="h-6 w-6 animate-spin text-teal-600" /> : items.map((a, i) => (
          <div key={a._id} className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid={`area-admin-card-${i}`}>
            <div>
              <p className="font-display text-sm font-bold text-navy">{a.name}</p>
              {a.suburbs && <p className="mt-0.5 text-xs text-slate-500">{a.suburbs}</p>}
            </div>
            <button type="button" onClick={() => remove(a._id)} className="focus-ring rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600" aria-label="Delete service area" data-testid={`area-delete-${i}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [form, setForm] = useState(null);
  useEffect(() => { api.get("/content/settings").then((r) => setForm({ phone: r.data.phone, email: r.data.email, abn: r.data.abn || "", google_review_url: r.data.google_review_url || "" })).catch(() => toast.error("Could not load settings")); }, []);

  const save = async (e) => {
    e.preventDefault();
    await api.patch("/admin/settings", form);
    toast.success("Settings saved — the website now shows these details");
  };

  if (!form) return <Loader2 className="h-6 w-6 animate-spin text-teal-600" />;
  return (
    <form onSubmit={save} className="max-w-xl rounded-2xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="settings-form">
      <h3 className="font-display text-base font-bold text-navy">Business Contact Details</h3>
      <p className="mt-1 text-xs text-slate-500">These replace the [PHONE NUMBER] and [EMAIL ADDRESS] placeholders across the whole website.</p>
      <div className="mt-6 space-y-5">
        <div>
          <label htmlFor="settings-phone" className="mb-1.5 block text-sm font-semibold text-navy">Phone Number</label>
          <input id="settings-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="settings-phone-input" />
        </div>
        <div>
          <label htmlFor="settings-email" className="mb-1.5 block text-sm font-semibold text-navy">Email Address</label>
          <input id="settings-email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="settings-email-input" />
        </div>
        <div>
          <label htmlFor="settings-abn" className="mb-1.5 block text-sm font-semibold text-navy">ABN</label>
          <input id="settings-abn" className={inputCls} value={form.abn} onChange={(e) => setForm({ ...form, abn: e.target.value })} placeholder="90 864 842 711" data-testid="settings-abn-input" />
          <p className="mt-1 text-xs text-slate-500">Shown on quotes, invoices, letterheads and the website footer.</p>
        </div>
        <div>
          <label htmlFor="settings-reviews" className="mb-1.5 block text-sm font-semibold text-navy">Google Reviews Link (optional)</label>
          <input id="settings-reviews" className={inputCls} value={form.google_review_url} onChange={(e) => setForm({ ...form, google_review_url: e.target.value })} placeholder="https://g.page/…" data-testid="settings-reviews-input" />
        </div>
        <button type="submit" className="focus-ring rounded-full bg-teal-600 px-6 py-2.5 text-sm font-bold text-white hover:bg-teal-700" data-testid="settings-save-button">Save Settings</button>
      </div>
    </form>
  );
}

export default function AdminDashboard() {
  useSeo("Admin Dashboard | RD Cleaning Services", "RD Cleaning Services admin dashboard.");
  const [tab, setTab] = useState("enquiries");
  const [email, setEmail] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setEmail(r.data.email))
      .catch(() => {
        localStorage.removeItem("rd_admin_token");
        navigate("/admin");
      });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("rd_admin_token");
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-navy">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5">
            <LogoMark size={30} dark />
            <span className="font-display text-sm font-extrabold tracking-tight text-white">RD Cleaning <span className="text-teal-400">Admin</span></span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-slate-400 sm:block">{email}</span>
            <Link to="/admin/documents" className="focus-ring rounded-full border border-white/20 px-4 py-1.5 text-xs font-semibold text-white hover:border-teal-300 hover:text-teal-300" data-testid="admin-documents-link">Documents</Link>
            <Link to="/" className="focus-ring rounded-full border border-white/20 px-4 py-1.5 text-xs font-semibold text-white hover:border-teal-300 hover:text-teal-300" data-testid="admin-view-site-link">View Site</Link>
            <button type="button" onClick={logout} className="focus-ring inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold text-white hover:bg-white/20" data-testid="admin-logout-button">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Admin sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={`focus-ring inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === t.id ? "bg-navy text-white" : "bg-white text-slate-600 hover:text-navy"}`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
        <div className="mt-8">
          {tab === "enquiries" && <EnquiriesTab />}
          {tab === "testimonials" && <TestimonialsTab />}
          {tab === "areas" && <AreasTab />}
          {tab === "settings" && <SettingsTab />}
        </div>
      </div>
    </div>
  );
}
